import React, { useCallback, useContext, useEffect, useState } from "react";
import apiRequest from "../../../../utils/apiClient";
import { messageContext } from "../../../../stores/messages/messageContext";
import { meetingCancelUrl, meetingFeedUrl, meetingsUrl } from "../../../constants";
import { buildMeetScript } from "../constants/meetScript";
import { MEETINGS_TEXT as T, MEETING_STATUS_LABEL } from "../constants/meetingsText";
import "./MeetingsTab.css";

/** One notetaker bot (transcript.models.MeetingBot.to_dict). */
export interface Meeting {
  id: number;
  meeting_url: string;
  platform: string;
  title: string;
  join_at: string | null;
  record_mode: "video" | "audio";
  recipients: string[];
  source: "link" | "invite" | "drive";
  status: string;
  status_detail: string;
  download_id: number | null;
  created_at: string | null;
}

const LIVE = new Set(["scheduled", "joining", "recording", "importing"]);
const POLL_MS = 20000;

const whenText = (m: Meeting): string => {
  const at = m.join_at || m.created_at;
  if (!at) return "";
  return new Date(at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

/**
 * My Library → Meetings: send CherryClipz Notetaker to a Meet / Zoom / Teams call (now or
 * later, video or audio only), or copy the address to add to calendar invites. The list
 * follows each bot from scheduled to emailed, polling while any is still live.
 */
const MeetingsTab = () => {
  const messageStore = useContext(messageContext);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [inviteAddress, setInviteAddress] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [url, setUrl] = useState("");
  const [mode, setMode] = useState<"video" | "audio">("video");
  const [later, setLater] = useState(false);
  const [joinAt, setJoinAt] = useState("");
  const [emails, setEmails] = useState("");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [feedUrl, setFeedUrl] = useState("");
  const [scriptCopied, setScriptCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiRequest(meetingsUrl, { method: "GET" });
      const body = await res.json();
      if (!res.ok) throw new Error(body.message);
      setMeetings(body.meetings || []);
      setInviteAddress(body.invite_address || "");
      setEnabled(Boolean(body.enabled));
    } catch {
      messageStore.setMessage(T.ERROR_LOAD, "error");
    }
  }, [messageStore]);

  useEffect(() => {
    load();
    apiRequest(meetingFeedUrl, { method: "GET" })
      .then((res) => (res.ok ? res.json() : {}))
      .then((body: { feed_url?: string }) => setFeedUrl(body.feed_url || ""))
      .catch(() => setFeedUrl(""));
  }, [load]);

  const anyLive = meetings.some((m) => LIVE.has(m.status));
  useEffect(() => {
    if (!anyLive) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [anyLive, load]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await apiRequest(meetingsUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          meeting_url: url.trim(),
          record_mode: mode,
          join_at: later && joinAt ? new Date(joinAt).toISOString() : null,
          emails,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || T.ERROR_SEND);
      messageStore.setMessage(body.meeting?.status === "scheduled" ? T.SENT_LATER : T.SENT_NOW, "success");
      setUrl("");
      setEmails("");
      setJoinAt("");
      await load();
    } catch (err) {
      messageStore.setMessage((err as Error).message || T.ERROR_SEND, "error");
    } finally {
      setSending(false);
    }
  };

  const cancel = async (id: number) => {
    const res = await apiRequest(meetingCancelUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      messageStore.setMessage(body.message || T.ERROR_SEND, "error");
    }
    load();
  };

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(inviteAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* the address is on screen to copy by hand */
    }
  };

  const copyScript = async () => {
    try {
      await navigator.clipboard.writeText(buildMeetScript(feedUrl));
      setScriptCopied(true);
      setTimeout(() => setScriptCopied(false), 2000);
    } catch {
      messageStore.setMessage(T.ERROR_LOAD, "error");
    }
  };

  return (
    <div className="cc-meet">
      {enabled && <p className="cc-meet-intro">{T.INTRO}</p>}

      <section className="cc-meet-card">
        <h3>{T.OWN_TITLE}</h3>
        <p className="cc-meet-note">{T.OWN_BODY}</p>
        <ol className="cc-meet-steps">
          <li>{T.OWN_STEP_1}</li>
          <li>{T.OWN_STEP_2}</li>
          <li>{T.OWN_STEP_3}</li>
        </ol>
        <div className="cc-meet-address">
          <button type="button" className="cc-meet-primary" disabled={!feedUrl} onClick={copyScript}>
            {scriptCopied ? T.COPIED : T.OWN_COPY}
          </button>
          <a className="cc-meet-ghost" href="https://script.google.com/home/projects/create" target="_blank" rel="noreferrer">
            {T.OWN_OPEN}
          </a>
        </div>
      </section>

      {enabled && (
        <>

      <section className="cc-meet-card">
        <h3>{T.AUTO_TITLE}</h3>
        <p className="cc-meet-note">{T.AUTO_BODY}</p>
        <div className="cc-meet-address">
          <code>{inviteAddress}</code>
          <button type="button" className="cc-meet-ghost" onClick={copyAddress}>
            {copied ? T.COPIED : T.COPY}
          </button>
        </div>
      </section>

      <form className="cc-meet-card" onSubmit={send}>
        <h3>{T.NOW_TITLE}</h3>
        <label className="cc-meet-label" htmlFor="cc-meet-url">{T.LINK_LABEL}</label>
        <input id="cc-meet-url" className="cc-meet-input" type="url" required value={url}
          placeholder={T.LINK_PLACEHOLDER} onChange={(e) => setUrl(e.target.value)} />

        <span className="cc-meet-label">{T.RECORD_LABEL}</span>
        <div className="cc-meet-pills">
          <button type="button" aria-pressed={mode === "video"} onClick={() => setMode("video")}>{T.RECORD_VIDEO}</button>
          <button type="button" aria-pressed={mode === "audio"} onClick={() => setMode("audio")}>{T.RECORD_AUDIO}</button>
        </div>

        <span className="cc-meet-label">{T.WHEN_LABEL}</span>
        <div className="cc-meet-pills">
          <button type="button" aria-pressed={!later} onClick={() => setLater(false)}>{T.WHEN_NOW}</button>
          <button type="button" aria-pressed={later} onClick={() => setLater(true)}>{T.WHEN_LATER}</button>
          {later && (
            <input className="cc-meet-input cc-meet-time" type="datetime-local" required value={joinAt}
              onChange={(e) => setJoinAt(e.target.value)} />
          )}
        </div>

        <label className="cc-meet-label" htmlFor="cc-meet-emails">{T.EMAILS_LABEL}</label>
        <input id="cc-meet-emails" className="cc-meet-input" value={emails}
          placeholder={T.EMAILS_PLACEHOLDER} onChange={(e) => setEmails(e.target.value)} />
        <p className="cc-meet-note">{T.EMAILS_NOTE}</p>

        <div>
          <button type="submit" className="cc-meet-primary" disabled={sending || !enabled || !url.trim()}>
            {sending ? T.SENDING : T.SEND}
          </button>
        </div>
        <p className="cc-meet-note">{T.CONSENT}</p>
      </form>
        </>
      )}

      <section className="cc-meet-list">
        <h3>{T.LIST_TITLE}</h3>
        {meetings.length === 0 && <p className="cc-meet-note">{T.EMPTY}</p>}
        {meetings.map((m) => (
          <div key={m.id} className={`cc-meet-row is-${m.status}`}>
            <div className="cc-meet-row-main">
              <div className="cc-meet-row-title">{m.title}</div>
              <div className="cc-meet-row-meta">
                {whenText(m)} · {m.record_mode === "audio" ? T.RECORD_AUDIO : T.RECORD_VIDEO}
                {m.recipients.length > 0 && ` · ${T.GUESTS(m.recipients.length)}`}
                {m.source === "invite" && ` · ${T.INVITE_SOURCE}`}
                {m.source === "drive" && ` · ${T.DRIVE_SOURCE}`}
              </div>
              {m.status === "failed" && m.status_detail && <div className="cc-meet-row-error">{m.status_detail}</div>}
            </div>
            <span className="cc-meet-state">{MEETING_STATUS_LABEL[m.status] || m.status}</span>
            {["scheduled", "joining", "recording"].includes(m.status) && (
              <button type="button" className="cc-meet-ghost" onClick={() => cancel(m.id)}>{T.CANCEL}</button>
            )}
          </div>
        ))}
      </section>
    </div>
  );
};

export default MeetingsTab;
